import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Copy from 'Src/components/Copy';
import TitleItems from 'Src/components/TitleItems';
import { CheckFromTime } from 'Src/feature/check/CheckFromTime';
import { TableRow } from 'Src/feature/data-table/TableRow';
import { FontIconRadio } from 'Src/feature/form/FontIconRadio';
import { FormField } from 'Src/feature/form/FormField';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalSubTitle } from 'Src/feature/modal/ModalSubTitle';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

import { useCheckMergeModal } from './useCheckMergeModal';

import styles from './CheckMergeModal.module.scss';

type CheckMergeModalProps = {
  open: boolean;
  close: () => void;
}

const CheckMergeModal: FC<CheckMergeModalProps> = (props) => {
  const { t } = useTranslation();
  const store = useCheckMergeModal(props);
  const { Table, Column } = store.table;

  return (
    <Modal
      open={props.open}
      loading={store.isLoading}
    >
      <ModalHeader>
        <ModalTitle copy={t('labels.merge')} />
        <ModalSubTitle copy={t('checkMerge.labels.selectDestination')} />
      </ModalHeader>

      <ModalContent>
        <div className={styles.content_wrapper}>
          <TitleItems
            className={styles.transferBar}
            items={store.transferBarItems}
          />
          <div className={styles.filter_bar}>
            <FormField
              type="select"
              variant="outlined"
              multiple
              fullWidth
              value={store.currentValues.revenueCenterId}
              options={store.options.revenueCenterId}
              onChange={(_evt, values) => store.filterHandlers.revenueCenterId(values as (string | number)[])}
            />
            <FormField
              type="select"
              variant="outlined"
              multiple
              fullWidth
              value={store.currentValues.orderTypeId}
              options={store.options.orderTypeId}
              onChange={(_evt, values) => store.filterHandlers.orderTypeId(values as (string | number)[])}
            />
            <FormField
              type="select"
              variant="outlined"
              multiple
              fullWidth
              value={store.currentValues.employeeId}
              options={store.options.employeeId}
              onChange={(_evt, values) => store.filterHandlers.employeeId(values as (string | number)[])}
            />
          </div>
          <Table
            pagination={<></>}
            classes={{ tableHeader: styles.tableHeader }}
            noDataMessage={t('checkMerge.labels.noChecks')}
            renderer={{
              row: (dataItem) => ({ children }) => (
                <TableRow
                  className={classNames(styles.tableRow, { [styles.tableRowSelected]: dataItem.id === store.selectedTargetCheck?.id })}
                  onClick={() => store.setSelectedTargetCheck(dataItem)}>
                  {children}
                </TableRow>
              ),
            }}
          >
            <Column
              key="radioCol"
              cellContentRenderer={({ dataItem }) => <FontIconRadio className={styles.radio} color="success" size="sm" checked={dataItem.id === store.selectedTargetCheck?.id}/>}
              minWidth={52}
              width={52}
            />
            <Column key="labelCol" label={t('labels.labelSlashGuest')} className={styles.labelCol} field="label" sortable minWidth={100}/>
            <Column key="tableNameCol" label={t('labels.table')} field="tableName" sortable minWidth={100} width={100}/>
            <Column
              key="revCenterCol"
              label={t('revenueCenter.shortLabel')}
              field="revCenter"
              cellContentRenderer={({ dataItem }) => dataItem.revCenter ? dataItem.revCenter : t('revenueCenter.labels.unknown')}
              sortable
              minWidth={100}
              width={230}
            />
            <Column key="checkNumberCol" label={t('check.labels.checkNumber')} field="number" sortable minWidth={100} width={100}/>
            <Column
              key="totalCol"
              label={t('check.labels.balance.total')}
              field="total"
              cellContentRenderer={({ dataItem }) => <Copy type="currency" value={dataItem.total}/>}
              sortable
              align="right"
              minWidth={100}
              width={100}
            />
            <Column
              key="timeOpenCol"
              label={t('labels.timeOpen')}
              field="timeOpen"
              cellContentRenderer={({ dataItem }) => <CheckFromTime timeMilli={dataItem.openedAt}/>}
              sortable
              minWidth={100}
              width={142}
            />
            <Column key="partySizeCol" label={t('labels.cover_plural')} field="partySize" sortable align="right" minWidth={100} width={100}/>
            <Column key="employeeShortNameCol" label={t('labels.owner')} field="employeeShortName" sortable minWidth={100} width={170}/>
          </Table>
        </div>
      </ModalContent>

      <ModalActions>
        <ModalAction label={t('labels.cancel')} color="secondary" onClick={props.close}/>
        <ModalAction label={t('labels.merge')} color="primary" onClick={store.handleMerge} disabled={!store.selectedTargetCheck}/>
      </ModalActions>
    </Modal>
  );
};

export { CheckMergeModal };
export type { CheckMergeModalProps };
